import { useCallback, useMemo, useState } from 'react';
import { LayoutChangeEvent } from 'react-native';
import { useSelector } from 'react-redux';
import { selectAnnotationsForPage } from '@reducers/v2/annotationsV2/annotationsV2.slice';
import type { ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';
import { getAngle } from '@v2/components/imageHandling/helpers';
import { fromOriginalCoordinates, normalizeRotation } from '@v2/components/imageHandling/components/DrawingCanvas/helpers';
import { useImageViewTransform } from '@v2/components/imageHandling/components/DrawingCanvas/hooks/useImageViewTransform';
import type { ManageAnnotationsOverlayResult, Viewport } from '@v2/components/imageHandling/components/DrawingCanvas/types';

export interface UseManageAnnotationsOverlayParams {
  image: ImageDocumentStoreItem;
  pageNumber: number;
}

/**
 * Layout state + derived data for the read-only AnnotationsOverlay. Holds the viewport size,
 * pulls saved annotations from the slice, rebases them into working space, and subscribes to
 * the shared image transform so the overlay tracks zoom/pan with the image.
 */
export const useManageAnnotationsOverlay = ({ image, pageNumber }: UseManageAnnotationsOverlayParams): ManageAnnotationsOverlayResult => {
  const [layout, setLayout] = useState<Viewport>({ viewportWidth: 0, viewportHeight: 0 });

  const handleLayout = useCallback((e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout;
    setLayout({ viewportWidth: width, viewportHeight: height });
  }, []);

  const select = useMemo(() => selectAnnotationsForPage(image.documentId, pageNumber), [image.documentId, pageNumber]);
  const storedAnnotations = useSelector(select);

  // Total rotation applied to the un-rotated bytes (covers cropped + customRotate via the project helper).
  const documentRotation = normalizeRotation(getAngle(image));

  const imageDimensions = useMemo(
    () => ({ widthOriginal: image.widthOriginal, heightOriginal: image.heightOriginal }),
    [image.widthOriginal, image.heightOriginal]
  );

  const rebased = useMemo(
    () =>
      storedAnnotations.map((annotation) =>
        fromOriginalCoordinates({ annotation, image: imageDimensions, viewport: layout, documentRotation })
      ),
    [storedAnnotations, imageDimensions, layout, documentRotation]
  );

  const viewTransform = useImageViewTransform(image.documentId);

  return {
    layout,
    handleLayout,
    rebased,
    hasAnnotations: storedAnnotations.length > 0,
    viewTransform,
    documentRotation,
  };
};
